"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const DEFAULT_GET_QUOTE_URL = "/upload";
const DEFAULT_IMAGE_SRC =
	"/image/new-version/machining/precision-machining/what-is-precision-cnc-machining.jpeg";

const DEFAULT_FEATURE_KEYS = [
	{ titleKey: "feature1Title", descKey: "feature1Desc" },
	{ titleKey: "feature2Title", descKey: "feature2Desc" },
] as const;

// 与 AxisMachineShop 一致的 Globe 图标组件
const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 ${
			className ?? "text-[#0d68ea]"
		}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

const CheckIcon = () => (
	<svg
		className="inline-block h-[1em] w-[1em] flex-shrink-0 align-middle"
		viewBox="0 0 20 20"
		fill="none"
		aria-hidden="true"
	>
		<circle cx="10" cy="10" r="10" fill="#64a525" />
		<path
			d="M6 10.5L8.5 13L14 7.5"
			stroke="white"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export interface ServiceIntroSectionProps {
	/** 翻译命名空间，需包含 titleLine1、titleLine2、introDesc、getQuote、imageAlt 及 feature*Title/feature*Desc */
	translationNamespace?: string;
	/** 标题是否合并为一行展示；默认保持两行，兼容现有页面 */
	singleLineTitle?: boolean;
	/** 左侧图片地址，不传则用 Precision Machining 默认图（与 carouselImageSrcs 二选一，轮播优先） */
	imageSrc?: string;
	/** 若传入且非空，左侧为多图自动轮播 + 指示点（如嵌件 vs 包胶对比图） */
	carouselImageSrcs?: readonly string[];
	/** 轮播自动切换间隔（毫秒），默认 5000 */
	carouselAutoPlayMs?: number;
	/** Get Quote 按钮链接，不传则用 /upload */
	getQuoteHref?: string;
	/** 是否隐藏 Get Quote 按钮 */
	hideGetQuote?: boolean;
	/** 特性列表的 key 对，不传则用 2 条（feature1/2） */
	featureKeys?: readonly { titleKey: string; descKey: string }[];
	/** 可选：段落 key，在特性列表下方、按钮上方渲染（如 Rapid Prototyping 的收尾句） */
	closingParagraphKey?: string;
}

/**
 * 通用「What is XXX」介绍区块：左图右文，标题+简介+多条✓特性+可选收尾段+Get Quote 按钮。
 * Precision Machining 与 Rapid Prototyping 等页共用。
 */
export default function PrecisionMachiningIntro({
	translationNamespace = "precisionMachiningIntroSection",
	singleLineTitle = false,
	imageSrc = DEFAULT_IMAGE_SRC,
	carouselImageSrcs,
	carouselAutoPlayMs = 5000,
	getQuoteHref = DEFAULT_GET_QUOTE_URL,
	hideGetQuote = false,
	featureKeys = DEFAULT_FEATURE_KEYS,
	closingParagraphKey,
}: ServiceIntroSectionProps) {
	const t = useTranslations(translationNamespace);
	const showCarousel = Boolean(carouselImageSrcs?.length);
	const slides = carouselImageSrcs;
	const [activeSlide, setActiveSlide] = useState(0);

	useEffect(() => {
		const n = slides?.length ?? 0;
		if (n === 0) return;
		setActiveSlide((i) => Math.min(i, n - 1));
	}, [slides?.length]);

	useEffect(() => {
		const n = slides?.length ?? 0;
		if (n < 2) return;
		const id = window.setInterval(() => {
			setActiveSlide((i) => (i + 1) % n);
		}, carouselAutoPlayMs);
		return () => window.clearInterval(id);
	}, [slides?.length, carouselAutoPlayMs]);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题与 Globe 图标同一行，图标在该行内居中（与 PrecisionMachineShopSection 一致） */}
				<div className="relative mb-8 flex min-h-[72px] items-center sm:mb-10 sm:min-h-[80px] md:mb-12 md:min-h-[88px]">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{singleLineTitle ? (
							<span>
								{t("titleLine1")} {t("titleLine2")}
							</span>
						) : (
							<>
								<span className="block">{t("titleLine1")}</span>
								<span className="block">{t("titleLine2")}</span>
							</>
						)}
					</h2>
					<div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
						<GlobeIcon className="!text-themePrimary400" />
					</div>
				</div>

				{/* 左侧图片 + 右侧文案与特性，各占一半 */}
				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-8 xl:gap-10">
					{/* 左侧图片 50%（轮播时点位于图片下方，不叠在图上） */}
					<div className="flex w-full flex-col lg:min-w-0 lg:flex-1">
						<div className="relative aspect-[4/3] w-full overflow-hidden shadow-lg lg:aspect-auto lg:h-full lg:min-h-[480px]">
							{showCarousel && slides ? (
								<Image
									key={slides[activeSlide]}
									src={slides[activeSlide]}
									alt={t("imageAlt")}
									fill
									className="object-cover"
									sizes="(max-width: 1024px) 100vw, 50vw"
									priority={activeSlide === 0}
								/>
							) : (
								<Image
									src={imageSrc}
									alt={t("imageAlt")}
									fill
									className="object-cover"
									sizes="(max-width: 1024px) 100vw, 50vw"
								/>
							)}
						</div>
						{showCarousel && slides && slides.length > 1 ? (
							<div className="mt-3 flex justify-center gap-2 px-2" role="tablist" aria-label="Image carousel">
								{slides.map((_, i) => (
									<button
										key={i}
										type="button"
										role="tab"
										aria-selected={i === activeSlide}
										aria-label={`${i + 1} / ${slides.length}`}
										onClick={() => setActiveSlide(i)}
										className={`h-2.5 w-2.5 rounded-full transition-all sm:h-3 sm:w-3 ${
											i === activeSlide
												? "scale-110 bg-themePrimary400 ring-2 ring-themePrimary400/25"
												: "bg-neutral-300 hover:bg-neutral-400"
										}`}
									/>
								))}
							</div>
						) : null}
					</div>

					{/* 右侧文案 50% */}
					<div className="flex w-full min-w-0 flex-1 flex-col justify-center lg:min-w-0">
						<p className="mb-6 text-sm leading-relaxed text-black md:text-base">{t("introDesc")}</p>
						<ul className="mb-8 space-y-5">
							{featureKeys.map((feature, index) => (
								<li key={index} className="text-sm leading-relaxed sm:text-base">
									<p className="mb-4">
										<CheckIcon />{" "}
										<span className="ml-4 text-base font-semibold text-black sm:text-lg">
											{t(feature.titleKey)}
										</span>
									</p>
									<p className="text-sm leading-relaxed text-black md:text-base">{t(feature.descKey)}</p>
								</li>
							))}
						</ul>
						{closingParagraphKey && (
							<p className="mb-6 text-sm leading-relaxed text-black md:text-base">{t(closingParagraphKey)}</p>
						)}
						{!hideGetQuote && (
							<a
								href={getQuoteHref}
								target="_blank"
								rel="noopener noreferrer"
								className="group inline-flex w-fit items-center justify-center gap-2 rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 sm:px-5 sm:py-3 md:px-6 md:py-3.5 md:text-base"
							>
								{t("getQuote")}
							</a>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}
